import { ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { STATUS_MAP, TEMPLATES, type CampaignRow } from '../types';

/**
 * Two compact inline filters for the campaign list toolbar — Status +
 * Campaign Type. Each is a popover with checkboxes. The trigger button shows
 * the count of active filters so the user can spot which lists are filtered.
 *
 * Designed to sit next to the search input via DataTable's `toolbar` slot.
 */
interface Props {
  selectedStatuses: string[];
  selectedTemplates: string[];
  onStatusChange: (values: string[]) => void;
  onTemplateChange: (values: string[]) => void;
}

export function CampaignFilters({
  selectedStatuses,
  selectedTemplates,
  onStatusChange,
  onTemplateChange,
}: Props) {
  return (
    <div className="flex items-center gap-2">
      <FilterPopover
        label="Status"
        options={Object.keys(STATUS_MAP).map((s) => ({ value: s, label: s }))}
        selected={selectedStatuses}
        onChange={onStatusChange}
      />
      <FilterPopover
        label="Campaign type"
        options={Object.entries(TEMPLATES).map(([id, lbl]) => ({
          value: id,
          label: lbl,
        }))}
        selected={selectedTemplates}
        onChange={onTemplateChange}
      />
    </div>
  );
}

function FilterPopover({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (v: string) => {
    if (selected.includes(v)) {
      onChange(selected.filter((s) => s !== v));
    } else {
      onChange([...selected, v]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          {label}
          {selected.length > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <div className="flex items-center justify-between px-2 py-1.5 text-xs text-muted-foreground">
          <span>{label}</span>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>
        <ul className="max-h-72 space-y-0.5 overflow-y-auto py-1">
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <li key={opt.value}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(opt.value)}
                  />
                  <span className="text-foreground">{opt.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Apply Status + Campaign Type filters to a row array.
 * Empty filter arrays = "no filter".
 */
export function applyCampaignFilters(
  rows: CampaignRow[],
  selectedStatuses: string[],
  selectedTemplates: string[],
): CampaignRow[] {
  if (selectedStatuses.length === 0 && selectedTemplates.length === 0) {
    return rows;
  }
  const allowedStatusCodes = new Set<number>();
  for (const label of selectedStatuses) {
    const code = STATUS_MAP[label];
    if (Array.isArray(code)) code.forEach((c) => allowedStatusCodes.add(c));
    else if (code !== undefined) allowedStatusCodes.add(code);
  }
  const allowedTemplateIds = new Set(
    selectedTemplates.map((t) => Number(t)),
  );
  return rows.filter((r) => {
    if (
      selectedStatuses.length > 0 &&
      !allowedStatusCodes.has(Number(r.campaignStatus))
    ) {
      return false;
    }
    if (
      selectedTemplates.length > 0 &&
      !allowedTemplateIds.has(Number(r.templateId))
    ) {
      return false;
    }
    return true;
  });
}

/** Helper — does any row carry retry data? Used to gate Retry columns + pill. */
export function rowsHaveRetries(rows: CampaignRow[]): boolean {
  return rows.some((r) => Number(r.retryCount ?? 0) > 0 || Number(r.retries ?? 0) > 0);
}

/** Helper — used by the Connected/Pending/DND/Retry cells to render the % suffix. */
export function pctOf(value: number | undefined | null, total: number): string {
  if (!total || !value) return '';
  const p = Math.round((Number(value) / total) * 100);
  return `${p}%`;
}
